import { readFileSync, readdirSync, existsSync } from "node:fs";
import { resolve, join, basename } from "node:path";
import type { CaiConfig, ScaffoldFrontmatter } from "./types.js";
import { getGit } from "./git.js";
import { copyToClipboardOrFile } from "./clipboard.js";
import { parseFrontmatter } from "./drift/frontmatter.js";
import { runCodex, runRepoBrief, isCodexStale } from "./codex/index.js";
import { stripFrontmatter } from "./utils/fs.js";

// Fallback: maps file-path patterns to relevant context files
export const RELEVANCE_MAP: Array<{ pattern: RegExp; contexts: string[] }> = [
  { pattern: /package\.json|\.toml|\.lock|go\.mod|Cargo\.toml|pom\.xml|Gemfile/, contexts: ["stack.md", "decisions.md"] },
  { pattern: /\.env|config\.|settings\.|\.yaml|\.yml/, contexts: ["setup.md", "architecture.md"] },
  { pattern: /migration|schema|model|entity|prisma/, contexts: ["architecture.md", "conventions.md"] },
  { pattern: /test|spec|__tests__/, contexts: ["conventions.md"] },
  { pattern: /route|controller|handler|endpoint|api/, contexts: ["architecture.md", "conventions.md"] },
  { pattern: /auth|session|token|jwt|oauth/, contexts: ["architecture.md", "decisions.md"] },
  { pattern: /component|hook|page|layout|view/, contexts: ["conventions.md", "architecture.md"] },
  { pattern: /deploy|docker|ci|cd|workflow|action/, contexts: ["setup.md", "decisions.md"] },
  { pattern: /DECISION|ROUTER|AGENTS|CLAUDE/, contexts: ["decisions.md"] },
];

// Fallback: maps focus query keywords to relevant context files
const FOCUS_MAP: Array<{ keywords: RegExp; contexts: string[] }> = [
  { keywords: /auth|login|jwt|oauth|token|session|permission|role/, contexts: ["architecture.md", "decisions.md"] },
  { keywords: /api|endpoint|route|rest|graphql|handler|controller/, contexts: ["architecture.md", "conventions.md"] },
  { keywords: /db|database|schema|migration|model|orm|prisma|sql/, contexts: ["architecture.md", "conventions.md"] },
  { keywords: /deploy|ci|cd|docker|k8s|kubernetes|infra|pipeline/, contexts: ["setup.md", "decisions.md"] },
  { keywords: /test|spec|coverage|unit|integration|e2e/, contexts: ["conventions.md"] },
  { keywords: /ui|component|frontend|design|style|css|layout|hook/, contexts: ["conventions.md", "architecture.md"] },
  { keywords: /refactor|cleanup|pattern|architecture|structure|module/, contexts: ["architecture.md", "conventions.md", "decisions.md"] },
  { keywords: /dep|dependency|package|upgrade|version|library/, contexts: ["stack.md", "decisions.md"] },
  { keywords: /perf|performance|cache|optim|speed|latency/, contexts: ["architecture.md", "decisions.md"] },
  { keywords: /config|setting|env|secret|key|feature.flag/, contexts: ["setup.md", "decisions.md"] },
];

export interface ContextFileInfo {
  filename: string;
  absPath: string;
  frontmatter: ScaffoldFrontmatter | null;
}

const MAX_CONTEXT_FILES = 6;

/** Discover all context files and parse their frontmatter triggers/edges */
export function discoverContextFiles(scaffoldRoot: string): ContextFileInfo[] {
  const contextDir = join(scaffoldRoot, "context");
  if (!existsSync(contextDir)) return [];

  const entries = readdirSync(contextDir).filter((f) => f.endsWith(".md"));
  return entries.map((filename) => {
    const absPath = join(contextDir, filename);
    return {
      filename,
      absPath,
      frontmatter: parseFrontmatter(absPath),
    };
  });
}

/** Match context files by frontmatter triggers against a search string */
function matchByTriggers(
  contextFiles: ContextFileInfo[],
  searchText: string
): string[] {
  const lower = searchText.toLowerCase();
  const matched: string[] = [];

  for (const { filename, frontmatter } of contextFiles) {
    if (!frontmatter) continue;
    const triggers = frontmatter.triggers as string[] | undefined;
    if (!Array.isArray(triggers)) continue;

    for (const trigger of triggers) {
      if (lower.includes(trigger.toLowerCase())) {
        matched.push(filename);
        break;
      }
    }
  }

  return matched;
}

/** 1-hop edge expansion: for each selected file, add edge targets whose condition matches */
function expandEdges(
  selectedFiles: Set<string>,
  contextFiles: ContextFileInfo[],
  searchText: string
): void {
  const lower = searchText.toLowerCase();
  const toAdd: string[] = [];

  for (const { filename, frontmatter } of contextFiles) {
    if (!selectedFiles.has(filename)) continue;
    if (!frontmatter?.edges) continue;

    for (const edge of frontmatter.edges) {
      // edge.target is like "context/conventions.md" — extract filename
      const target = basename(edge.target);
      if (selectedFiles.has(target)) continue;
      if (selectedFiles.size + toAdd.length >= MAX_CONTEXT_FILES) break;

      // Match edge condition against search text, or include if no condition
      if (!edge.condition || lower.includes(edge.condition.toLowerCase().replace(/^when\s+/i, ""))) {
        toAdd.push(target);
      }
    }
  }

  for (const t of toAdd) selectedFiles.add(t);
}
export interface SessionResult {
  prompt: string;
  copiedToClipboard: boolean;
  fallbackPath: string | null;
  contextFilesLoaded: string[];
  modifiedFiles: string[];
  recentCommits: string[];
  focus?: string;
  codexRefreshed: boolean;
}

export async function runSession(
  config: CaiConfig,
  opts: { copy?: boolean; focus?: string } = {}
): Promise<SessionResult> {
  const git = getGit(config.projectRoot);

  // Get current git state
  const [status, log] = await Promise.all([
    git.status(),
    git.log({ maxCount: 5 }),
  ]);

  const modifiedFiles = [
    ...status.modified,
    ...status.staged,
    ...status.not_added,
    ...status.created,
  ].filter((f) => !f.startsWith(".cai/"));

  const recentCommits = log.all.map((c: { hash: string; message: string }) => `${c.hash.slice(0, 7)} ${c.message}`);

  // Select relevant context files — frontmatter triggers first, static maps as fallback
  const contextSet = new Set<string>();
  const contextFiles = discoverContextFiles(config.scaffoldRoot);

  // Build combined search text from focus query + modified file paths
  const searchParts: string[] = [];
  if (opts.focus) searchParts.push(opts.focus);
  searchParts.push(...modifiedFiles);
  const searchText = searchParts.join(" ");

  // 1. Frontmatter trigger matching (dynamic — works for custom context files too)
  if (searchText) {
    const triggerMatches = matchByTriggers(contextFiles, searchText);
    for (const m of triggerMatches) {
      if (contextSet.size >= MAX_CONTEXT_FILES) break;
      contextSet.add(m);
    }
  }

  // 2. Static map fallback (for projects that haven't added triggers yet)
  if (opts.focus) {
    const query = opts.focus.toLowerCase();
    for (const { keywords, contexts } of FOCUS_MAP) {
      if (keywords.test(query)) {
        contexts.forEach((c) => { if (contextSet.size < MAX_CONTEXT_FILES) contextSet.add(c); });
      }
    }
  }
  for (const file of modifiedFiles) {
    for (const { pattern, contexts } of RELEVANCE_MAP) {
      if (pattern.test(file)) {
        contexts.forEach((c) => { if (contextSet.size < MAX_CONTEXT_FILES) contextSet.add(c); });
      }
    }
  }

  // 3. Edge expansion — 1-hop traversal of selected files' edges
  if (contextSet.size > 0 && contextSet.size < MAX_CONTEXT_FILES) {
    expandEdges(contextSet, contextFiles, searchText);
  }

  // Load context file contents (first 40 lines each — enough for signal, saves tokens)
  const contextDir = join(config.scaffoldRoot, "context");
  const loadedFiles: string[] = [];
  const contextSections: string[] = [];

  for (const filename of contextSet) {
    const filePath = join(contextDir, filename);
    if (!existsSync(filePath)) continue;
    const raw = readFileSync(filePath, "utf-8");
    const stripped = stripFrontmatter(raw);
    const lines = stripped.split("\n").slice(0, 40).join("\n").trim();
    if (!lines) continue;
    loadedFiles.push(filename);
    contextSections.push(`### ${filename}\n${lines}`);
  }

  // Auto-refresh codex if stale, then inject into prompt
  let codexRefreshed = false;
  const codeMapSections: string[] = [];
  const codexDir = join(config.scaffoldRoot, "codex");

  if (isCodexStale(config)) {
    try {
      await runCodex(config);
      await runRepoBrief(config);
      codexRefreshed = true;
    } catch {
      // Silent — codex is optional
    }
  }

  for (const codexFile of ["modules.md", "repo-brief.md"]) {
    const codexPath = join(codexDir, codexFile);
    if (!existsSync(codexPath)) continue;
    const raw = readFileSync(codexPath, "utf-8");
    // Strip generated header
    const stripped = raw.replace(/^<!--.*?-->\n*/, "");
    const trimmed = stripped.split("\n").slice(0, 40).join("\n").trim();
    if (trimmed) codeMapSections.push(`### ${codexFile}\n${trimmed}`);
  }

  // Get project name from manifest if available
  const projectName = getProjectName(config.projectRoot);

  // Build compact prompt
  const prompt = buildSessionPrompt({
    projectName,
    modifiedFiles,
    recentCommits,
    contextSections,
    codeMapSections,
    projectRoot: config.projectRoot,
    focus: opts.focus,
  });

  const clip = opts.copy !== false
    ? copyToClipboardOrFile(prompt, "cai-session-prompt.md")
    : { ok: false, fallbackPath: null };

  return {
    prompt,
    copiedToClipboard: clip.ok,
    fallbackPath: clip.fallbackPath,
    contextFilesLoaded: loadedFiles,
    modifiedFiles,
    recentCommits,
    focus: opts.focus,
    codexRefreshed,
  };
}

function buildSessionPrompt(opts: {
  projectName: string | null;
  modifiedFiles: string[];
  recentCommits: string[];
  contextSections: string[];
  codeMapSections?: string[];
  projectRoot: string;
  focus?: string;
}): string {
  const parts: string[] = [];

  parts.push(`# Session${opts.projectName ? ` — ${opts.projectName}` : ""}`);
  parts.push(`> This session context is ephemeral. After context compaction, only CLAUDE.md persists. Key rules belong there.`);

  if (opts.focus) {
    parts.push(`> Focus: ${opts.focus}`);
  }

  if (opts.modifiedFiles.length > 0) {
    const shown = opts.modifiedFiles.slice(0, 10);
    const overflow = opts.modifiedFiles.length - shown.length;
    const suffix = overflow > 0 ? `\n- +${overflow} more` : "";
    parts.push(`## Active files\n${shown.map((f) => `- ${f}`).join("\n")}${suffix}`);
  }

  if (opts.recentCommits.length > 0) {
    const shown = opts.recentCommits.slice(0, 3);
    parts.push(`## Recent commits\n${shown.map((c) => `- ${c}`).join("\n")}`);
  }

  if (opts.contextSections.length > 0) {
    parts.push(`## Context\n${opts.contextSections.join("\n")}`);
  }

  if (opts.codeMapSections && opts.codeMapSections.length > 0) {
    parts.push(`## Code Map\n${opts.codeMapSections.join("\n")}`);
  }

  return parts.join("\n\n");
}

export function printSessionResult(result: SessionResult): void {
  console.log();
  console.log("cai session — context snapshot built");
  console.log();

  if (result.modifiedFiles.length > 0) {
    const shown = result.modifiedFiles.slice(0, 5);
    const overflow = result.modifiedFiles.length - shown.length;
    console.log(`  Active files: ${shown.join(", ")}${overflow > 0 ? ` +${overflow} more` : ""}`);
  } else {
    console.log("  Working tree clean — no modified files");
  }

  if (result.contextFilesLoaded.length > 0) {
    console.log(`  Context loaded: ${result.contextFilesLoaded.join(", ")}`);
    if (result.focus) {
      console.log(`  ℹ Context was selected based on focus topic: "${result.focus}"`);
    } else {
      console.log("  ℹ These files were selected because your modified files match known patterns");
      console.log("    (e.g. schema files → architecture.md, config files → setup.md).");
    }
  } else if (result.modifiedFiles.length > 0) {
    console.log("  Context loaded: none");
    if (result.focus) {
      console.log(`  ℹ No context files matched focus topic "${result.focus}" — only base session info included.`);
    } else {
      console.log("  ℹ No modified files matched known patterns — only base session info included.");
    }
  } else {
    console.log("  Context loaded: none (clean working tree)");
  }

  if (result.codexRefreshed) {
    console.log("  Code map: refreshed (modules.md + repo-brief.md)");
  }

  console.log(`  Prompt size: ~${Math.ceil(result.prompt.length / 4).toLocaleString()} tokens`);
  console.log();

  if (result.copiedToClipboard) {
    console.log("✔ Session prompt copied to clipboard");
    console.log("  ℹ Paste into your AI tool to start with full project context.");
  } else if (result.fallbackPath) {
    console.log("⚠ Clipboard unavailable — prompt saved to:");
    console.log(`    ${result.fallbackPath}`);
    console.log("  ℹ Open the file and paste its contents into your AI tool.");
  } else {
    console.log("⚠ Clipboard not available — run with --print to output the prompt directly.");
  }

  console.log();
  console.log("  ℹ This prompt will be compacted away in long sessions (~83% context usage).");
  console.log("    Put critical rules in CLAUDE.md — it reloads after every compaction.");
  console.log();
}

function getProjectName(projectRoot: string): string | null {
  try {
    const pkg = JSON.parse(readFileSync(resolve(projectRoot, "package.json"), "utf-8"));
    return pkg.name ?? null;
  } catch {
    // Try go.mod
    try {
      const gomod = readFileSync(resolve(projectRoot, "go.mod"), "utf-8");
      return gomod.match(/^module\s+(\S+)/m)?.[1] ?? null;
    } catch {
      return null;
    }
  }
}
